"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, MessageCircle, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DealerApplicationGateway() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="24289" id="book-consultation" ref={ref} className={`relative w-full py-24 bg-background text-foreground transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-secondary mb-4 tracking-tight">
            Schedule Your Free Sleep Consultation
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Take the first step toward restorative, CPAP-free sleep in Los Angeles. 
            Our specialists usually respond to all inquiries within 2 hours.
          </p>
        </div>

        <Card className="max-w-[800px] mx-auto shadow-xl border-t-4 border-t-primary bg-card rounded-2xl overflow-hidden">
          <CardHeader className="pb-2 text-center border-b border-border/50 bg-muted/20">
            <CardTitle className="text-2xl font-bold text-secondary">
              Consultation Request Form
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 md:p-8">
            <FormProvider formKey="consultation_form_contact" formId="consultation_form_contact" sectionName="dealer-application-gateway" defaultValues={{
            full_name: "",
            company_name: "",
            city: "",
            phone_number: "",
            email_address: "",
            budget: "",
            message: ""
          }} validationRules={{
            full_name: {
              required: true,
              minLength: 2
            },
            company_name: {
              required: true,
              minLength: 2
            },
            city: {
              required: true
            },
            phone_number: {
              required: true,
              pattern: "phone"
            },
            email_address: {
              required: true,
              pattern: "email"
            },
            budget: {
              required: true
            },
            message: {
              required: false
            }
          }} submitText="Book My Free Consultation" successMessage="Consultation request received! Our Los Angeles team will contact you within 2 hours." errorMessage="Failed to submit request. Please call us directly or try again.">
              <DealerForm />
            </FormProvider>
          </CardContent>
        </Card>
      </div>

      {}
      <div className="fixed bottom-6 right-6 z-50">
        <Link to="https://wa.me/13105550123" newTab className="flex items-center justify-center w-14 h-14 bg-[#22c55e] text-white rounded-full shadow-[0_0_20px_rgba(34,197,94,0.5)] hover:scale-110 transition-transform duration-300 hover:shadow-[0_0_25px_rgba(34,197,94,0.7)]" aria-label="Book Free Consultation on WhatsApp">
          <MessageCircle className="w-8 h-8 fill-current" />
        </Link>
      </div>
    </section>;
}
function DealerForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-green-100 text-[#22c55e] rounded-full flex items-center justify-center mb-2">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-bold text-secondary">Request Received!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-primary text-primary hover:bg-primary/5 rounded-full" onClick={() => window.location.reload()}>
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold">
                Full Name <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="Jane Doe" className="h-12 bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold">
                Insurance Provider{" "}
                <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Blue Shield, Aetna" className="h-12 bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="city" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold">
                City / Neighborhood <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g. Santa Monica" className="h-12 bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-secondary font-semibold">
                Phone Number <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(310) 555-0123" className="h-12 bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="email_address" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">
              Email Address <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" className="h-12 bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">
              Primary Sleep Concern{" "}
              <span className="text-destructive">*</span>
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-12 bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20">
                  <SelectValue placeholder="Select your main symptom" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="50000">Chronic Snoring</SelectItem>
                <SelectItem value="100000">Daytime Fatigue</SelectItem>
                <SelectItem value="200000">Gasping for Air</SelectItem>
                <SelectItem value="500000">Diagnosed Sleep Apnea</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-secondary font-semibold">
              Additional Details (Optional)
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your symptoms or ask a question..." className="min-h-[120px] bg-background border-primary/30 rounded-2xl focus:border-primary focus:ring-primary/20 resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-2xl animate-in fade-in slide-in-from-top-2">
          <p className="text-destructive text-sm font-medium text-center">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-full">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending Request...
          </> : "Book My Free Consultation"}
      </Button>
    </Form>;
}